import { useState, useEffect } from 'react';

const MemoryGame = () => {
  const emojis = ['🌟', '🎈', '🦋', '🌸', '🍀', '🎨', '🎵', '⚡'];
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [timer, setTimer] = useState(0);

  // Inicializar el juego
  const initializeGame = () => {
    const shuffledCards = [...emojis, ...emojis]
      .sort(() => Math.random() - 0.5)
      .map((emoji, index) => ({
        id: index,
        emoji,
        isFlipped: false,
        isMatched: false
      }));
    
    setCards(shuffledCards);
    setFlippedCards([]);
    setMatchedCards([]);
    setMoves(0);
    setGameWon(false);
    setGameStarted(false);
    setTimer(0);
  };

  // Timer
  useEffect(() => {
    let interval;
    if (gameStarted && !gameWon) {
      interval = setInterval(() => {
        setTimer(timer => timer + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStarted, gameWon]);

  // Inicializar cuando se monta el componente
  useEffect(() => {
    initializeGame();
  }, []);

  // Manejar clic en carta
  const handleCardClick = (clickedCard) => {
    if (!gameStarted) setGameStarted(true);
    
    if (
      flippedCards.length === 2 ||
      flippedCards.includes(clickedCard.id) ||
      matchedCards.includes(clickedCard.id)
    ) {
      return;
    }

    const newFlippedCards = [...flippedCards, clickedCard.id];
    setFlippedCards(newFlippedCards);

    if (newFlippedCards.length === 2) {
      setMoves(moves + 1);
      
      const [firstId, secondId] = newFlippedCards;
      const firstCard = cards.find(card => card.id === firstId);
      const secondCard = cards.find(card => card.id === secondId);

      if (firstCard.emoji === secondCard.emoji) {
        setTimeout(() => {
          const newMatchedCards = [...matchedCards, firstId, secondId];
          setMatchedCards(newMatchedCards);
          setFlippedCards([]);
          
          if (newMatchedCards.length === cards.length) {
            setGameWon(true);
          }
        }, 800);
      } else {
        setTimeout(() => {
          setFlippedCards([]);
        }, 1200);
      }
    }
  };

  const isCardVisible = (card) => {
    return flippedCards.includes(card.id) || matchedCards.includes(card.id);
  };

  const isCardMatched = (card) => {
    return matchedCards.includes(card.id);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPerformanceMessage = () => {
    if (moves <= 12) return { text: "¡Memoria extraordinaria! 🧠✨", color: "text-purple-600" };
    if (moves <= 18) return { text: "¡Excelente trabajo! 🎯", color: "text-blue-600" };
    if (moves <= 25) return { text: "¡Bien hecho! 👏", color: "text-green-600" };
    return { text: "¡Completado! 🎉", color: "text-orange-600" };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 p-3 sm:p-6">
        {/* Mobile Layout */}
        <div className="lg:hidden min-h-screen flex items-center justify-center">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 max-w-2xl w-full mx-2 sm:mx-4">
            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-3 sm:mb-4 shadow-lg">
                <span className="text-2xl sm:text-3xl">🧠</span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent mb-2">
                Memory Master
              </h1>
              <p className="text-white/80 text-sm sm:text-base md:text-lg px-2">
                Desafía tu mente y encuentra todas las parejas
              </p>
            </div>

            {/* Stats Dashboard */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-2xl p-2 sm:p-4 text-center">
                <div className="text-lg sm:text-2xl font-bold text-white">{moves}</div>
                <div className="text-white/60 text-xs sm:text-sm uppercase tracking-wide">Movimientos</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-2xl p-2 sm:p-4 text-center">
                <div className="text-lg sm:text-2xl font-bold text-white">{formatTime(timer)}</div>
                <div className="text-white/60 text-xs sm:text-sm uppercase tracking-wide">Tiempo</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-2xl p-2 sm:p-4 text-center">
                <div className="text-lg sm:text-2xl font-bold text-white">{Math.floor((matchedCards.length / 16) * 100)}%</div>
                <div className="text-white/60 text-xs sm:text-sm uppercase tracking-wide">Progreso</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6 sm:mb-8">
              <div className="bg-white/20 rounded-full h-2 sm:h-3 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500 ease-out"
                  style={{ width: `${(matchedCards.length / 16) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Game Board */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-6 sm:mb-8">
              {cards.map((card) => (
                <div
                  key={card.id}
                  onClick={() => handleCardClick(card)}
                  className={`
                    aspect-square rounded-xl sm:rounded-2xl cursor-pointer transform transition-all duration-500 hover:scale-105 relative overflow-hidden
                    ${isCardVisible(card) 
                      ? isCardMatched(card)
                        ? 'bg-gradient-to-br from-emerald-400 to-teal-500 shadow-xl sm:shadow-2xl shadow-emerald-500/25 animate-pulse'
                        : 'bg-gradient-to-br from-blue-500 to-purple-600 shadow-xl sm:shadow-2xl shadow-blue-500/25'
                      : 'bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30'
                    }
                  `}
                >
                  <div className={`
                    w-full h-full flex items-center justify-center transition-transform duration-500
                    ${isCardVisible(card) ? 'transform-none' : 'transform rotateY-180'}
                  `}>
                    {isCardVisible(card) ? (
                      <div className="relative">
                        <span className="text-2xl sm:text-3xl md:text-4xl animate-bounce filter drop-shadow-lg">
                          {card.emoji}
                        </span>
                        {isCardMatched(card) && (
                          <div className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 bg-emerald-400 rounded-full flex items-center justify-center">
                            <span className="text-xs sm:text-xs text-white">✓</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg mb-1 sm:mb-2 mx-auto flex items-center justify-center">
                          <span className="text-white text-sm sm:text-lg">?</span>
                        </div>
                        <div className="w-4 h-0.5 sm:w-6 sm:h-1 bg-white/30 rounded mx-auto"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Action Button */}
            <div className="text-center mb-4 sm:mb-6">
              <button
                onClick={initializeGame}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg shadow-xl sm:shadow-2xl hover:shadow-purple-500/25 transform hover:scale-105 transition-all duration-300 border border-white/20"
              >
                <span className="mr-2">🔄</span>
                Nuevo Juego
              </button>
            </div>

            {/* Footer */}
            <div className="text-center text-white/50 text-xs sm:text-sm border-t border-white/10 pt-4 sm:pt-6">
              <p className="mb-2">Haz clic en las cartas para revelar los símbolos ocultos</p>
              <div className="flex justify-center items-center flex-wrap gap-3 sm:gap-4">
                <span className="flex items-center">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-purple-500 rounded-full mr-1 sm:mr-2"></div>
                  <span className="text-xs sm:text-sm">Volteada</span>
                </span>
                <span className="flex items-center">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-emerald-500 rounded-full mr-1 sm:mr-2"></div>
                  <span className="text-xs sm:text-sm">Emparejada</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex min-h-screen items-center justify-center gap-8 xl:gap-12">
          {/* Left Panel - Stats & Controls */}
          <div className="flex flex-col space-y-6 w-80">
            {/* Header */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4 shadow-lg">
                <span className="text-3xl">🧠</span>
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent mb-2">
                Memory Master
              </h1>
              <p className="text-white/80 text-base">
                Desafía tu mente y encuentra todas las parejas
              </p>
            </div>

            {/* Stats Dashboard */}
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center">
                <div className="text-4xl font-bold text-white mb-2">{moves}</div>
                <div className="text-white/60 text-sm uppercase tracking-wide">Movimientos</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center">
                <div className="text-4xl font-bold text-white mb-2">{formatTime(timer)}</div>
                <div className="text-white/60 text-sm uppercase tracking-wide">Tiempo</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center">
                <div className="text-4xl font-bold text-white mb-2">{Math.floor((matchedCards.length / 16) * 100)}%</div>
                <div className="text-white/60 text-sm uppercase tracking-wide">Progreso</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
              <div className="text-white/80 text-sm mb-3 text-center">Progreso del Juego</div>
              <div className="bg-white/20 rounded-full h-4 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500 ease-out"
                  style={{ width: `${(matchedCards.length / 16) * 100}%` }}
                ></div>
              </div>
              <div className="text-center text-white/60 text-xs mt-2">
                {matchedCards.length / 2} de 8 parejas encontradas
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={initializeGame}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-purple-500/25 transform hover:scale-105 transition-all duration-300 border border-white/20"
            >
              <span className="mr-2">🔄</span>
              Nuevo Juego
            </button>
          </div>

          {/* Center Panel - Game Board */}
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-8">
            <div className="grid grid-cols-4 gap-4 w-96 h-96">
              {cards.map((card) => (
                <div
                  key={card.id}
                  onClick={() => handleCardClick(card)}
                  className={`
                    aspect-square rounded-2xl cursor-pointer transform transition-all duration-500 hover:scale-105 relative overflow-hidden
                    ${isCardVisible(card) 
                      ? isCardMatched(card)
                        ? 'bg-gradient-to-br from-emerald-400 to-teal-500 shadow-2xl shadow-emerald-500/25 animate-pulse'
                        : 'bg-gradient-to-br from-blue-500 to-purple-600 shadow-2xl shadow-blue-500/25'
                      : 'bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30'
                    }
                  `}
                >
                  <div className={`
                    w-full h-full flex items-center justify-center transition-transform duration-500
                    ${isCardVisible(card) ? 'transform-none' : 'transform rotateY-180'}
                  `}>
                    {isCardVisible(card) ? (
                      <div className="relative">
                        <span className="text-4xl animate-bounce filter drop-shadow-lg">
                          {card.emoji}
                        </span>
                        {isCardMatched(card) && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center">
                            <span className="text-xs text-white">✓</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg mb-2 mx-auto flex items-center justify-center">
                          <span className="text-white text-lg">?</span>
                        </div>
                        <div className="w-6 h-1 bg-white/30 rounded mx-auto"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Instructions & Legend */}
          <div className="flex flex-col space-y-6 w-80">
            {/* Instructions */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 text-center">¿Cómo jugar?</h3>
              <div className="space-y-3 text-white/80 text-sm">
                <div className="flex items-start space-x-3">
                  <span className="text-purple-400 font-bold">1.</span>
                  <span>Haz clic en cualquier carta para voltearla</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-purple-400 font-bold">2.</span>
                  <span>Busca su pareja haciendo clic en otra carta</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-purple-400 font-bold">3.</span>
                  <span>Si coinciden, se quedan volteadas</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-purple-400 font-bold">4.</span>
                  <span>¡Encuentra todas las parejas!</span>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 text-center">Leyenda</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 bg-white/20 rounded-lg"></div>
                  <span className="text-white/80 text-sm">Carta oculta</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg"></div>
                  <span className="text-white/80 text-sm">Carta volteada</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg"></div>
                  <span className="text-white/80 text-sm">Pareja encontrada</span>
                </div>
              </div>
            </div>

            {/* Performance Tips */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 text-center">💡 Consejos</h3>
              <div className="space-y-2 text-white/80 text-sm">
                <p>• Concéntrate en recordar las posiciones</p>
                <p>• Menos de 15 movimientos = ¡Experto!</p>
                <p>• Tómate tu tiempo, no hay prisa</p>
                <p>• Practica para mejorar tu memoria</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Win Modal Overlay */}
      {gameWon && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/20 backdrop-blur-xl border border-white/30 rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-md w-full text-center">
            <div className="text-6xl sm:text-8xl mb-4 sm:mb-6 animate-bounce">🏆</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
              ¡Victoria!
            </h2>
            <div className={`text-lg sm:text-xl mb-4 sm:mb-6 ${getPerformanceMessage().color}`}>
              {getPerformanceMessage().text}
            </div>
            
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="text-xl sm:text-2xl font-bold text-white">{moves}</div>
                <div className="text-white/60 text-xs sm:text-sm">Movimientos</div>
              </div>
              <div className="bg-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="text-xl sm:text-2xl font-bold text-white">{formatTime(timer)}</div>
                <div className="text-white/60 text-xs sm:text-sm">Tiempo</div>
              </div>
            </div>

            <button
              onClick={initializeGame}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg shadow-xl sm:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-white/20 w-full sm:w-auto"
            >
              Jugar de Nuevo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryGame;